import { __awaiter, __generator } from "tslib";
import { fetchSeasonPreviewImageSource } from './packIndexFetch';
import { collectSeasonPreviewUrls, sampleSeasonPreviewUrls, SEASON_PREVIEW_LOAD_BATCH, SEASON_PREVIEW_LOGIN_MAX_SLIDES, SEASON_PREVIEW_MAX_SLIDES, uniqueUrlsByStyle, } from './seasonPreviewCollectUrls';
var ROOT_ID_OVERLAY = 'molo-season-preview';
var ROOT_ID_BACKGROUND = 'molo-season-preview-background';
var MARQUEE_PX_PER_SEC = 36;
var MARQUEE_MEASURE_FALLBACK_MS = 2000;
var DRAG_CLICK_THRESHOLD_PX = 6;
function inferSeasonFromPath() {
    var match = window.location.pathname.match(/\/shop\/([^/]+)/i);
    return match ? decodeURIComponent(match[1]) : null;
}
function shuffleUrls(urls) {
    var list = urls.slice();
    for (var i = list.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1));
        var tmp = list[i];
        list[i] = list[j];
        list[j] = tmp;
    }
    return list;
}
function wrapMarqueeOffset(offset, halfWidth) {
    if (halfWidth <= 0) {
        return 0;
    }
    var wrapped = offset % halfWidth;
    if (wrapped < 0) {
        wrapped += halfWidth;
    }
    return wrapped;
}
function marqueeSpeedPxPerSec() {
    var reduced = typeof window !== 'undefined' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return reduced ? MARQUEE_PX_PER_SEC * 0.35 : MARQUEE_PX_PER_SEC;
}
function measureMarqueeHalfWidth(track) {
    return track.scrollWidth / 2;
}
function readTrackOffsetPx(track) {
    var transform = window.getComputedStyle(track).transform;
    if (!transform || transform === 'none') {
        return 0;
    }
    try {
        var matrix = new DOMMatrixReadOnly(transform);
        return Math.max(0, -matrix.m41);
    }
    catch (_a) {
        return 0;
    }
}
function applyManualTransform(track, offsetPx) {
    track.style.transform = 'translate3d(' + String(-offsetPx) + 'px, 0, 0)';
}
function stopCssMarquee(track) {
    var offsetPx = readTrackOffsetPx(track);
    track.classList.remove('season-preview__track--auto');
    track.style.animation = 'none';
    track.style.removeProperty('--marquee-delay');
    track.style.animationPlayState = '';
    applyManualTransform(track, offsetPx);
    return offsetPx;
}
function startCssMarquee(track, offsetPx, halfWidth) {
    if (halfWidth <= 0) {
        return;
    }
    var pxPerSec = marqueeSpeedPxPerSec();
    var durationSec = Math.max(60, halfWidth / pxPerSec);
    var wrapped = wrapMarqueeOffset(offsetPx, halfWidth);
    var delaySec = -(wrapped / halfWidth) * durationSec;
    track.classList.remove('season-preview__track--auto');
    track.style.removeProperty('transform');
    track.style.removeProperty('animation');
    void track.offsetHeight;
    track.style.setProperty('--marquee-duration', String(durationSec) + 's');
    track.style.setProperty('--marquee-shift', String(halfWidth) + 'px');
    track.style.setProperty('--marquee-delay', String(delaySec) + 's');
    track.classList.add('season-preview__track--auto');
}
import { createMoloLogoProductSvg } from './moloLogoProductSvg';
var LOGIN_SPLASH_HIDE_MS = 720;
var LOGIN_MIN_SPLASH_MS = 280;
var LOGIN_REVEAL_FALLBACK_MS = 4000;
var LOGIN_POST_REVEAL_MEASURE_MS = 2800;
var LOGIN_LOAD_REST_DELAY_MS = 1800;
function ensureLoginSplash(parent) {
    var splash = parent.querySelector('.season-preview-login-splash');
    if (splash) {
        return splash;
    }
    splash = document.createElement('div');
    splash.className = 'season-preview-login-splash';
    splash.setAttribute('aria-hidden', 'true');
    var logoWrap = document.createElement('div');
    logoWrap.className = 'season-preview-login-splash__logo-wrap';
    logoWrap.style.color = '#000';
    var logo = createMoloLogoProductSvg({
        className: 'season-preview-login-splash__logo molo-logo-blend__svg',
        width: 240,
        height: 91,
    });
    logoWrap.appendChild(logo);
    splash.appendChild(logoWrap);
    parent.insertBefore(splash, parent.firstChild);
    return splash;
}
function revealLoginBackground(parent, root, splash) {
    parent.classList.add('season-preview-login-host--revealed');
    root.classList.add('season-preview--revealed');
    var target = splash || parent.querySelector('.season-preview-login-splash');
    if (!target) {
        return;
    }
    target.classList.add('season-preview-login-splash--hidden');
    window.setTimeout(function () {
        if (target.parentElement) {
            target.remove();
        }
    }, LOGIN_SPLASH_HIDE_MS);
}
var SeasonPreviewController = /** @class */ (function () {
    function SeasonPreviewController() {
        this.onKeyDown = null;
        this.marqueeEngine = null;
        this.running = false;
        this.mode = 'overlay';
        this.mountParent = null;
    }
    SeasonPreviewController.prototype.isRunning = function () {
        return this.running;
    };
    SeasonPreviewController.prototype.start = function (seasonCode) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.mount('overlay', document.body, seasonCode || inferSeasonFromPath() || undefined)];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    SeasonPreviewController.prototype.mountBackground = function (parent, seasonCode, preloadedUrls) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.mount('background', parent, seasonCode, preloadedUrls)];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    SeasonPreviewController.prototype.mountWithUrls = function (mode, parent, seasonCode, preloadedUrls) {
        return __awaiter(this, void 0, void 0, function () {
            var allUrls, _a, stripUrls, totalCount;
            return __generator(this, function (_b) {
                this.stop();
                allUrls = shuffleUrls(uniqueUrlsByStyle(preloadedUrls.filter(Boolean)));
                if (!allUrls.length) {
                    console.warn('[SeasonPreview] Ingen preloaded billeder for', seasonCode);
                    return [2 /*return*/];
                }
                _a = sampleSeasonPreviewUrls(allUrls, mode === 'background' ? SEASON_PREVIEW_LOGIN_MAX_SLIDES : SEASON_PREVIEW_MAX_SLIDES), stripUrls = _a.stripUrls, totalCount = _a.totalCount;
                this.mode = mode;
                this.mountParent = parent;
                this.running = true;
                this.mountDom(seasonCode, stripUrls, totalCount, mode, parent);
                if (mode === 'background') {
                    console.info('[SeasonPreview] Baggrund', seasonCode, '·', stripUrls.length, '/', totalCount, 'billeder');
                }
                return [2 /*return*/];
            });
        });
    };
    SeasonPreviewController.prototype.mount = function (mode, parent, seasonCodeHint, preloadedUrls) {
        return __awaiter(this, void 0, void 0, function () {
            var source, allUrls, _a, stripUrls, totalCount;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!(preloadedUrls && preloadedUrls.length)) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.mountWithUrls(mode, parent, seasonCodeHint || 'SALESBOOK', preloadedUrls)];
                    case 1:
                        _b.sent();
                        return [2 /*return*/];
                    case 2:
                        this.stop();
                        return [4 /*yield*/, fetchSeasonPreviewImageSource(seasonCodeHint)];
                    case 3:
                        source = _b.sent();
                        allUrls = shuffleUrls(collectSeasonPreviewUrls(source.imageMap));
                        if (!allUrls.length) {
                            console.warn('[SeasonPreview] Ingen billeder fundet for', source.seasonCode);
                            return [2 /*return*/];
                        }
                        _a = sampleSeasonPreviewUrls(allUrls, mode === 'background' ? SEASON_PREVIEW_LOGIN_MAX_SLIDES : SEASON_PREVIEW_MAX_SLIDES), stripUrls = _a.stripUrls, totalCount = _a.totalCount;
                        this.mode = mode;
                        this.mountParent = parent;
                        this.running = true;
                        this.mountDom(source.seasonCode, stripUrls, totalCount, mode, parent);
                        if (mode === 'overlay') {
                            console.info('[SeasonPreview]', source.seasonCode, '·', totalCount, 'billeder ·', stripUrls.length, 'i smøren — træk/klik · ESC');
                        }
                        else {
                            console.info('[SeasonPreview] Baggrund', source.seasonCode, '·', stripUrls.length, '/', totalCount, 'billeder');
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    SeasonPreviewController.prototype.stop = function () {
        this.running = false;
        if (this.marqueeEngine) {
            this.marqueeEngine.dispose();
            this.marqueeEngine = null;
        }
        if (this.onKeyDown) {
            window.removeEventListener('keydown', this.onKeyDown);
            this.onKeyDown = null;
        }
        document.body.classList.remove('season-preview--active');
        var overlay = document.getElementById(ROOT_ID_OVERLAY);
        if (overlay) {
            overlay.remove();
        }
        var bg = document.getElementById(ROOT_ID_BACKGROUND);
        if (bg) {
            bg.remove();
        }
        this.mountParent = null;
        var hosts = document.querySelectorAll('.season-preview-login-host--revealed');
        hosts.forEach(function (host) {
            host.classList.remove('season-preview-login-host--revealed');
        });
    };
    SeasonPreviewController.prototype.applyAutoScroll = function (engine) {
        if (!engine.autoPlay || engine.halfWidth <= 0) {
            return;
        }
        var track = engine.track;
        var alreadyAuto = track.classList.contains('season-preview__track--auto');
        if (engine.postReveal && alreadyAuto) {
            var pxPerSec = marqueeSpeedPxPerSec();
            var durationSec = Math.max(60, engine.halfWidth / pxPerSec);
            track.style.setProperty('--marquee-duration', String(durationSec) + 's');
            track.style.setProperty('--marquee-shift', String(engine.halfWidth) + 'px');
            return;
        }
        var prevShift = parseFloat(track.style.getPropertyValue('--marquee-shift') || '0');
        if (alreadyAuto &&
            prevShift > 0 &&
            Math.abs(engine.halfWidth - prevShift) / prevShift < 0.04) {
            var pxPerSec = marqueeSpeedPxPerSec();
            var durationSec = Math.max(60, engine.halfWidth / pxPerSec);
            track.style.setProperty('--marquee-duration', String(durationSec) + 's');
            track.style.setProperty('--marquee-shift', String(engine.halfWidth) + 'px');
            return;
        }
        startCssMarquee(track, engine.offsetPx, engine.halfWidth);
    };
    SeasonPreviewController.prototype.canRemeasureAfterReveal = function (engine) {
        if (!engine.postReveal || engine.revealAt <= 0) {
            return true;
        }
        return Date.now() - engine.revealAt >= LOGIN_POST_REVEAL_MEASURE_MS;
    };
    SeasonPreviewController.prototype.syncMarqueeFromTrack = function (engine, preserveProgress) {
        var prevHalf = engine.halfWidth;
        var progress = preserveProgress && prevHalf > 0 ? engine.offsetPx / prevHalf : 0;
        var nextHalf = measureMarqueeHalfWidth(engine.track);
        if (nextHalf <= 0) {
            return false;
        }
        engine.halfWidth = nextHalf;
        engine.offsetPx = preserveProgress
            ? wrapMarqueeOffset(progress * nextHalf, nextHalf)
            : wrapMarqueeOffset(engine.offsetPx, nextHalf);
        if (engine.deferAnimationUntilReveal) {
            return true;
        }
        if (engine.isDragging) {
            applyManualTransform(engine.track, engine.offsetPx);
            return true;
        }
        if (engine.autoPlay) {
            this.applyAutoScroll(engine);
        }
        else {
            stopCssMarquee(engine.track);
            applyManualTransform(engine.track, engine.offsetPx);
        }
        return true;
    };
    SeasonPreviewController.prototype.scheduleMarqueeMeasure = function (engine) {
        var _this = this;
        if (!this.canRemeasureAfterReveal(engine)) {
            return;
        }
        if (engine.remeasureTimer !== null) {
            window.clearTimeout(engine.remeasureTimer);
        }
        engine.remeasureTimer = window.setTimeout(function () {
            engine.remeasureTimer = null;
            if (!_this.running) {
                return;
            }
            _this.syncMarqueeFromTrack(engine, true);
        }, 280);
    };
    SeasonPreviewController.prototype.startMarqueeEngine = function (marquee, track, interactive, hooks, options) {
        var _this = this;
        if (this.marqueeEngine) {
            this.marqueeEngine.dispose();
        }
        var engine = {
            track: track,
            marquee: marquee,
            offsetPx: 0,
            halfWidth: 0,
            autoPlay: true,
            interactive: interactive,
            isDragging: false,
            dragMoved: false,
            dragStartX: 0,
            dragStartOffset: 0,
            measureFallbackTimer: null,
            remeasureTimer: null,
            marqueeStarted: false,
            deferAnimationUntilReveal: Boolean(options && options.holdAnimationUntilReveal),
            postReveal: false,
            revealAt: 0,
            dispose: function () { return undefined; },
        };
        var bootAttempts = 0;
        var tryStartAutoScroll = function (preserveProgress) {
            if (!_this.running) {
                return;
            }
            if (_this.syncMarqueeFromTrack(engine, preserveProgress)) {
                if (!engine.marqueeStarted) {
                    engine.marqueeStarted = true;
                    if (hooks && hooks.onMarqueeStarted) {
                        hooks.onMarqueeStarted();
                    }
                }
                if (engine.measureFallbackTimer !== null) {
                    window.clearTimeout(engine.measureFallbackTimer);
                    engine.measureFallbackTimer = null;
                }
                return;
            }
            bootAttempts++;
            if (bootAttempts < 120) {
                window.requestAnimationFrame(function () { return tryStartAutoScroll(preserveProgress); });
            }
        };
        var onPointerDown = function (e) {
            if (!interactive || e.button !== 0) {
                return;
            }
            engine.isDragging = true;
            engine.dragMoved = false;
            engine.dragStartX = e.clientX;
            if (engine.autoPlay && engine.halfWidth > 0) {
                engine.offsetPx = stopCssMarquee(track);
            }
            else {
                engine.offsetPx = readTrackOffsetPx(track);
                stopCssMarquee(track);
            }
            engine.dragStartOffset = engine.offsetPx;
            marquee.classList.add('season-preview__marquee--dragging');
            if (marquee.setPointerCapture) {
                marquee.setPointerCapture(e.pointerId);
            }
            e.preventDefault();
        };
        var onPointerMove = function (e) {
            if (!engine.isDragging || engine.halfWidth <= 0) {
                return;
            }
            var dx = e.clientX - engine.dragStartX;
            if (Math.abs(dx) > DRAG_CLICK_THRESHOLD_PX) {
                engine.dragMoved = true;
            }
            engine.offsetPx = wrapMarqueeOffset(engine.dragStartOffset - dx, engine.halfWidth);
            applyManualTransform(track, engine.offsetPx);
        };
        var endDrag = function (e) {
            if (!engine.isDragging) {
                return;
            }
            engine.isDragging = false;
            marquee.classList.remove('season-preview__marquee--dragging');
            if (marquee.releasePointerCapture) {
                try {
                    marquee.releasePointerCapture(e.pointerId);
                }
                catch (_a) {
                    /* released */
                }
            }
            if (!engine.dragMoved) {
                engine.autoPlay = !engine.autoPlay;
                marquee.classList.toggle('season-preview__marquee--paused', !engine.autoPlay);
                engine.offsetPx = readTrackOffsetPx(track);
                if (engine.autoPlay && engine.halfWidth > 0) {
                    _this.applyAutoScroll(engine);
                }
                else {
                    stopCssMarquee(track);
                }
                return;
            }
            if (engine.autoPlay && engine.halfWidth > 0) {
                _this.applyAutoScroll(engine);
            }
            else {
                applyManualTransform(track, engine.offsetPx);
            }
        };
        if (interactive) {
            marquee.addEventListener('pointerdown', onPointerDown);
            marquee.addEventListener('pointermove', onPointerMove);
            marquee.addEventListener('pointerup', endDrag);
            marquee.addEventListener('pointercancel', endDrag);
        }
        var imgs = Array.from(track.querySelectorAll('img.season-preview__slide-img'));
        var settledCount = 0;
        var totalImages = imgs.length;
        var settledImages = new WeakSet();
        var onImageSettled = function (img) {
            if (settledImages.has(img)) {
                return;
            }
            settledImages.add(img);
            settledCount++;
            if (hooks && hooks.onImageSettled) {
                hooks.onImageSettled(img);
            }
            if (engine.deferAnimationUntilReveal) {
                var nextHalf = measureMarqueeHalfWidth(engine.track);
                if (nextHalf > 0) {
                    engine.halfWidth = nextHalf;
                    if (!engine.marqueeStarted) {
                        engine.marqueeStarted = true;
                        if (hooks && hooks.onMarqueeStarted) {
                            hooks.onMarqueeStarted();
                        }
                    }
                }
                return;
            }
            if (engine.marqueeStarted) {
                if (_this.canRemeasureAfterReveal(engine)) {
                    _this.scheduleMarqueeMeasure(engine);
                }
                return;
            }
            if (settledCount === 1 ||
                settledCount === 4 ||
                settledCount === 12 ||
                settledCount >= totalImages) {
                tryStartAutoScroll(settledCount > 1);
            }
            else {
                _this.scheduleMarqueeMeasure(engine);
            }
        };
        imgs.forEach(function (img) {
            if (!img.getAttribute('src')) {
                img.addEventListener('load', function () { return onImageSettled(img); }, { once: true });
                img.addEventListener('error', function () { return onImageSettled(img); }, { once: true });
                return;
            }
            if (img.complete) {
                onImageSettled(img);
                return;
            }
            img.addEventListener('load', function () { return onImageSettled(img); }, { once: true });
            img.addEventListener('error', function () { return onImageSettled(img); }, { once: true });
        });
        window.requestAnimationFrame(function () {
            tryStartAutoScroll(false);
        });
        engine.measureFallbackTimer = window.setTimeout(function () {
            tryStartAutoScroll(false);
        }, MARQUEE_MEASURE_FALLBACK_MS);
        engine.dispose = function () {
            if (engine.measureFallbackTimer !== null) {
                window.clearTimeout(engine.measureFallbackTimer);
                engine.measureFallbackTimer = null;
            }
            if (engine.remeasureTimer !== null) {
                window.clearTimeout(engine.remeasureTimer);
                engine.remeasureTimer = null;
            }
            if (interactive) {
                marquee.removeEventListener('pointerdown', onPointerDown);
                marquee.removeEventListener('pointermove', onPointerMove);
                marquee.removeEventListener('pointerup', endDrag);
                marquee.removeEventListener('pointercancel', endDrag);
            }
        };
        this.marqueeEngine = engine;
    };
    SeasonPreviewController.prototype.mountDom = function (season, stripUrls, totalCount, mode, parent) {
        var _this = this;
        var rootId = mode === 'background' ? ROOT_ID_BACKGROUND : ROOT_ID_OVERLAY;
        var existing = document.getElementById(rootId);
        if (existing) {
            existing.remove();
        }
        var root = document.createElement('div');
        root.id = rootId;
        root.className =
            'season-preview' + (mode === 'background' ? ' season-preview--background' : '');
        if (mode === 'overlay') {
            root.setAttribute('role', 'dialog');
            root.setAttribute('aria-modal', 'true');
            root.setAttribute('aria-label', 'Season preview');
        }
        else {
            root.setAttribute('aria-hidden', 'true');
        }
        var stage = document.createElement('div');
        stage.className = 'season-preview__stage';
        var marquee = document.createElement('div');
        marquee.className = 'season-preview__marquee';
        if (mode === 'overlay') {
            marquee.setAttribute('aria-label', 'Træk for at scrolle billeder. Klik for at pause eller genoptage.');
        }
        var track = document.createElement('div');
        track.className = 'season-preview__track';
        var batchSize = SEASON_PREVIEW_LOAD_BATCH;
        var deferBatches = mode === 'background';
        var batchCount = Math.ceil(stripUrls.length / batchSize);
        var trackImages = [];
        var batchesReady = new Set();
        var loadBatch = function (batchIndex) {
            if (batchIndex >= batchCount) {
                return;
            }
            trackImages.forEach(function (img) {
                if (Number(img.dataset.batch) !== batchIndex) {
                    return;
                }
                var url = img.dataset.src;
                if (url && !img.getAttribute('src')) {
                    img.src = url;
                }
            });
        };
        var loadAllRemainingBatches = function () {
            for (var batchIndex = 1; batchIndex < batchCount; batchIndex++) {
                loadBatch(batchIndex);
            }
        };
        var markBatchReady = function (batchIndex) {
            if (batchesReady.has(batchIndex)) {
                return false;
            }
            var batchImgs = trackImages.filter(function (img) {
                return Number(img.dataset.batch) === batchIndex && img.dataset.strip === '0';
            });
            var allSettled = batchImgs.every(function (img) {
                if (!img.getAttribute('src')) {
                    return false;
                }
                return img.complete;
            });
            if (!allSettled) {
                return false;
            }
            batchesReady.add(batchIndex);
            return true;
        };
        var appendImages = function (list, passIndex) {
            var fragment = document.createDocumentFragment();
            list.forEach(function (url, index) {
                var batchIndex = Math.floor(index / batchSize);
                var img = document.createElement('img');
                img.className = 'season-preview__slide-img';
                img.alt = '';
                img.draggable = false;
                img.setAttribute('aria-hidden', 'true');
                img.decoding = 'async';
                img.dataset.src = url;
                img.dataset.batch = String(batchIndex);
                img.dataset.strip = String(passIndex);
                if (!deferBatches || batchIndex === 0) {
                    img.src = url;
                }
                trackImages.push(img);
                fragment.appendChild(img);
            });
            track.appendChild(fragment);
        };
        appendImages(stripUrls, 0);
        appendImages(stripUrls, 1);
        marquee.appendChild(track);
        stage.appendChild(marquee);
        root.appendChild(stage);
        var interactive = mode === 'overlay';
        var loginSplash = null;
        var marqueeReady = false;
        var backgroundRevealed = false;
        var revealScheduled = false;
        var mountAt = Date.now();
        var batch0StripReady = function () {
            var strip0Batch0 = trackImages.filter(function (img) {
                return img.dataset.strip === '0' && Number(img.dataset.batch) === 0;
            });
            if (!strip0Batch0.length) {
                return false;
            }
            return strip0Batch0.every(function (img) {
                return Boolean(img.getAttribute('src')) && img.complete;
            });
        };
        if (mode === 'background') {
            loginSplash = ensureLoginSplash(parent);
            var beginBackgroundReveal_1 = function () {
                var engine = _this.marqueeEngine;
                if (!engine || backgroundRevealed) {
                    return;
                }
                engine.deferAnimationUntilReveal = false;
                engine.postReveal = true;
                engine.revealAt = Date.now();
                engine.offsetPx = 0;
                _this.syncMarqueeFromTrack(engine, false);
                backgroundRevealed = true;
                revealLoginBackground(parent, root, loginSplash);
                window.setTimeout(function () {
                    if (_this.running) {
                        loadAllRemainingBatches();
                    }
                }, LOGIN_LOAD_REST_DELAY_MS);
                window.setTimeout(function () {
                    var eng = _this.marqueeEngine;
                    if (_this.running && eng && eng.postReveal) {
                        _this.syncMarqueeFromTrack(eng, true);
                    }
                }, LOGIN_LOAD_REST_DELAY_MS + 1200);
            };
            var maybeRevealBackground_1 = function () {
                if (backgroundRevealed || revealScheduled || !marqueeReady || !batch0StripReady()) {
                    return;
                }
                revealScheduled = true;
                var wait = Math.max(0, LOGIN_MIN_SPLASH_MS - (Date.now() - mountAt));
                window.setTimeout(function () {
                    if (backgroundRevealed || !_this.running) {
                        return;
                    }
                    beginBackgroundReveal_1();
                }, wait);
            };
            window.setTimeout(function () {
                if (backgroundRevealed || revealScheduled) {
                    return;
                }
                var engine = _this.marqueeEngine;
                if (!engine) {
                    return;
                }
                if (!marqueeReady) {
                    var nextHalf = measureMarqueeHalfWidth(engine.track);
                    if (nextHalf > 0) {
                        engine.halfWidth = nextHalf;
                        engine.marqueeStarted = true;
                        marqueeReady = true;
                    }
                }
                if (!batch0StripReady() && !marqueeReady) {
                    return;
                }
                revealScheduled = true;
                beginBackgroundReveal_1();
            }, LOGIN_REVEAL_FALLBACK_MS);
            this.startMarqueeEngine(marquee, track, interactive, {
                onImageSettled: function (img) {
                    if (!deferBatches) {
                        return;
                    }
                    var batchIndex = Number(img.dataset.batch);
                    if (isNaN(batchIndex)) {
                        return;
                    }
                    markBatchReady(batchIndex);
                    maybeRevealBackground_1();
                },
                onMarqueeStarted: function () {
                    marqueeReady = true;
                    maybeRevealBackground_1();
                },
            }, { holdAnimationUntilReveal: true });
        }
        else {
            this.startMarqueeEngine(marquee, track, interactive);
        }
        if (mode === 'overlay') {
            var meta = document.createElement('div');
            meta.className = 'season-preview__meta';
            var countLabel = totalCount > stripUrls.length
                ? totalCount + ' billeder · ' + stripUrls.length + ' i smøren'
                : String(totalCount) + ' billeder';
            meta.textContent =
                season.toUpperCase() + ' · ' + countLabel + ' · træk · klik pause · ESC';
            var closeBtn = document.createElement('button');
            closeBtn.type = 'button';
            closeBtn.className = 'season-preview__close';
            closeBtn.setAttribute('aria-label', 'Close season preview');
            closeBtn.textContent = '×';
            closeBtn.addEventListener('click', function () { return _this.stop(); });
            root.appendChild(meta);
            root.appendChild(closeBtn);
            document.body.classList.add('season-preview--active');
            this.onKeyDown = function (e) {
                if (e.key === 'Escape') {
                    _this.stop();
                }
            };
            window.addEventListener('keydown', this.onKeyDown);
        }
        parent.appendChild(root);
    };
    return SeasonPreviewController;
}());
var controller = new SeasonPreviewController();
export function mountSeasonPreviewBackground(parent, seasonCode) {
    return controller.mountBackground(parent, seasonCode);
}
export function mountSeasonPreviewBackgroundWithUrls(parent, seasonCode, imageUrls) {
    return controller.mountBackground(parent, seasonCode, imageUrls);
}
export function unmountSeasonPreviewBackground() {
    controller.stop();
}
export function installSeasonPreviewConsole() {
    var api = {
        start: function (seasonCode) { return controller.start(seasonCode); },
        stop: function () { return controller.stop(); },
        isRunning: function () { return controller.isRunning(); },
    };
    window.SeasonPreview = api;
    console.info('%cMolo SeasonPreview%c — SeasonPreview.start("W25") · SeasonPreview.stop()', 'font-weight:bold', 'font-weight:normal');
    return api;
}
export { persistLastSeasonCode } from './packIndexFetch';
//# sourceMappingURL=seasonPreviewController.js.map